import { Checkbox } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchHouseholds from 'src/Hooks/Queries/useFetchHouseholds';
import { EventDtoTypeEnum } from 'src/Services/API/generated';
import { trackPlanningFilterGroupToggled } from 'src/Services/Mixpanel/events';
import { useEventFiltersStore } from 'src/Stores/eventFiltersStore';
import { FALLBACK_HOUSEHOLD_COLOR, FILTER_GROUPS, FilterGroupKey } from 'src/Utils/eventVisuals';

import styles from './Filters.module.scss';

const ALL_TYPES = Object.values(EventDtoTypeEnum);

const EVENT_TYPE_GROUPS: FilterGroupKey[] = ['sejours', 'maison', 'reperes'];

const Filters = () => {
  const { t } = useTranslation();
  const { data: households } = useFetchHouseholds();

  const {
    selectedEventTypes,
    setSelectedEventTypes,
    showPracticalReminders,
    setShowPracticalReminders,
    showRooms,
    setShowRooms,
  } = useEventFiltersStore();

  const effectiveSelected = useMemo<Set<EventDtoTypeEnum>>(
    () => new Set(selectedEventTypes.length === 0 ? ALL_TYPES : selectedEventTypes),
    [selectedEventTypes]
  );

  const isGroupChecked = (group: FilterGroupKey): boolean =>
    FILTER_GROUPS[group].every(type => effectiveSelected.has(type));

  const toggleGroup = (group: FilterGroupKey) => {
    const currentlyChecked = isGroupChecked(group);
    const groupTypes = FILTER_GROUPS[group];
    const next = new Set(effectiveSelected);

    if (currentlyChecked) {
      groupTypes.forEach(type => next.delete(type));
    } else {
      groupTypes.forEach(type => next.add(type));
    }

    // Normalize: if every type is present, store as [] (= all).
    if (ALL_TYPES.every(type => next.has(type))) {
      setSelectedEventTypes([]);
    } else {
      setSelectedEventTypes(Array.from(next));
    }

    trackPlanningFilterGroupToggled({ group, enabled: !currentlyChecked });
  };

  const toggleReminders = () => {
    const next = !showPracticalReminders;
    setShowPracticalReminders(next);
    trackPlanningFilterGroupToggled({ group: 'reminders', enabled: next });
  };

  const toggleRooms = () => {
    const next = !showRooms;
    setShowRooms(next);
    trackPlanningFilterGroupToggled({ group: 'rooms', enabled: next });
  };

  return (
    <div className={styles.container}>
      <div className={styles.groupList}>
        {EVENT_TYPE_GROUPS.map(group => (
          <Checkbox
            key={group}
            checked={isGroupChecked(group)}
            onChange={() => toggleGroup(group)}
            label={t(`planning.filters.groups.${group}`)}
            className={styles.checkbox}
          />
        ))}
        <Checkbox
          checked={showPracticalReminders}
          onChange={toggleReminders}
          label={t('planning.filters.reminders')}
          className={styles.checkbox}
        />
        <Checkbox
          checked={showRooms}
          onChange={toggleRooms}
          label={t('planning.filters.rooms', 'Chambres')}
          className={styles.checkbox}
        />
      </div>

      <div className={styles.householdsSection}>
        <div className={styles.householdsTitle}>{t('planning.filters.householdsTitle')}</div>
        {households && households.length > 0 ? (
          <ul className={styles.householdList}>
            {households.map(household => (
              <li key={household.id} className={styles.householdItem}>
                <span
                  className={styles.householdDot}
                  style={{ backgroundColor: household.color ?? FALLBACK_HOUSEHOLD_COLOR }}
                />
                <span className={styles.householdName}>{household.name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.householdsEmpty}>{t('planning.filters.householdsEmpty')}</div>
        )}
      </div>
    </div>
  );
};

export default Filters;
